import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const iconProps = {
  width: 28,
  height: 28,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const VALUES = [
  {
    title: "Practical, not flashy",
    desc:
      "We build software small teams can actually use on day one. No multi-month rollouts, no jargon, no priesthood.",
    points: [
      "Onboard in days, not months",
      "Train staff in a single afternoon",
      "No consultants required",
    ],
    icon: (
      <svg {...iconProps}>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    title: "Customer-first, always",
    desc:
      "Our customers' success is the only metric that matters. We invest in onboarding, training, and the long relationship.",
    points: [
      "Onboarding and training included",
      "A real person replies — never a bot",
      "We measure success by your time saved",
    ],
    icon: (
      <svg {...iconProps}>
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
      </svg>
    ),
  },
  {
    title: "Yours, securely",
    desc:
      "Your business data belongs to you. We protect it like our own and make sure you can always take it with you.",
    points: [
      "Encrypted at rest and in transit",
      "Daily backups across regions",
      "One-click data export, any time",
    ],
    icon: (
      <svg {...iconProps}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    ),
  },
  {
    title: "Built for India",
    desc:
      "We know how Indian businesses actually run — the compliance, the workflows, the realities. The software reflects that.",
    points: [
      "GST, TDS, and e-invoicing ready",
      "Bilingual UI in Hindi and English",
      "Local payment gateways integrated",
    ],
    icon: (
      <svg {...iconProps}>
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
      </svg>
    ),
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const About = () => (
  <>
    <section className="page-hero">
      <motion.div
        className="page-hero__inner"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <p className="eyebrow">About Byteflow</p>
        <h1>We help small businesses run like big ones.</h1>
        <p className="page-hero__lead">
          Byteflow started in 2017 with a simple belief: the software that
          powers large enterprises shouldn’t be out of reach for small
          businesses. Today we build four products — ERP, Courier, Medical,
          and School — for industries we know well, with the same DNA across
          all of them.
        </p>
      </motion.div>
    </section>

    <section className="section">
      <motion.div
        className="section__header"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <p className="eyebrow">Our values</p>
        <h2>What we stand for</h2>
        <p className="section__lead">
          Four principles that show up in every product decision, every
          customer call, and every line of code we ship.
        </p>
      </motion.div>

      <div className="values-grid">
        {VALUES.map((v, i) => (
          <motion.article
            key={v.title}
            className="value-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: i * 0.07 }}
          >
            <header className="value-card__head">
              <span className="value-card__num">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="value-card__icon">{v.icon}</span>
            </header>
            <h3>{v.title}</h3>
            <p className="value-card__desc">{v.desc}</p>
            <ul className="value-card__points">
              {v.points.map((point) => (
                <li key={point}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </motion.article>
        ))}
      </div>
    </section>

    <section className="section section--muted">
      <div className="story">
        <motion.div
          className="story__text"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <p className="eyebrow">Our story</p>
          <h2>From a fasteners workshop to a four-product suite.</h2>
          <p>
            Byteflow began as a single internal tool for a Ludhiana fasteners
            workshop that couldn’t justify the cost of a traditional ERP. Once
            the owner’s neighbours started asking for it, we knew we had
            something worth building properly.
          </p>
          <p>
            That first product became Byteflow ERP — still our flagship today.
            Over the years our customers pulled us into new industries:
            courier companies needed tracking and route planning, clinics
            needed EMR and billing, schools needed admissions and fees. So we
            built those too.
          </p>
          <p>
            Today, more than a hundred small businesses run Byteflow every day
            across four product lines. Same belief, same DNA.
          </p>
          <Link to="/contact" className="btn-primary">Talk to us</Link>
        </motion.div>
        <motion.div
          className="story__image"
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <img src="/about-erp.jpg" alt="Byteflow team at work" />
        </motion.div>
      </div>
    </section>
  </>
);

export default About;
